import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import { Marker, Popup } from "react-leaflet";
import trackableListingCard from "../../analytics/trackables/trackable";
import { useCurrencies } from "../../spages/spa/context/CurrenciesContext";
import { urlMaker } from "../../utils/url";
import ImageGallery from "../ImageGallery/ImageGallery";
import ListingDetailsBrief from "../ListingDetailsBrief/ListingDetailsBrief";
import ShortlistButton from "../Shortlist/ShortlistButton";
import StudentExclusivePill from "../StudentExclusivePill/StudentExclusivePill";

const propTypes = {
  t: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
  listing: PropTypes.object,
  position: PropTypes.array,
  highlight: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
  onPopup: PropTypes.func,
  event: PropTypes.func.isRequired,
  isSmallScreen: PropTypes.bool,
  getListingTitle: PropTypes.func,
  getListingUrl: PropTypes.func,
  trackable: PropTypes.bool,
  trackingCallback: PropTypes.func,
  canModifyWishlist: PropTypes.bool,
  category: PropTypes.shape({
    label: PropTypes.string,
  }),
  selectedListing: PropTypes.string,
  tracker: PropTypes.object.isRequired,
  userId: PropTypes.string,
};

const ListingMarker = ({
  t,
  lang,
  position,
  listing,
  isSmallScreen,
  getListingTitle,
  getListingUrl,
  trackable,
  canModifyWishlist = true,
  category,
  highlight,
  selectedListing,
  event,
  tracker,
  userId,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onPopup,
  trackingCallback,
}) => {
  const [isMarkerActive, setIsMarkerActive] = useState(false);
  const { formatCurrency } = useCurrencies();
  const markerRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.options.riseOnHover = true;
    }
  }, []);

  useEffect(() => {
    setIsMarkerActive(selectedListing === listing.groupId);
  }, [selectedListing, listing.groupId]);

  const handleHover = () => {
    if (onMouseEnter) {
      onMouseEnter(listing);
    }
  };

  const handleLeaveHover = () => {
    if (onMouseLeave) {
      onMouseLeave(listing);
    }
  };

  const handleClickListing = (listing) => {
    setIsMarkerActive(selectedListing === listing.id);
    event("go to listing details from map", listing.groupId);
    tracker.events.trackListingOnMapClicked({
      userId,
      groupId: listing.groupId,
    });
  };

  const handleSelectImage = () => {
    event("scroll image gallery on map");
  };

  const handlePopup = (isOpen) => {
    if (onPopup) {
      onPopup(isOpen ? listing : null);
    }
    setIsMarkerActive(isOpen);
    event("click listing on map", listing.groupId);
  };

  const isHomesForUkrainiansCategory =
    category && category.label === "CATEGORY_HOMES_FOR_UKRAINIANS";

  // there seems to be no easy way to simply add an ID to a marker icon,
  // so the below 'hack' allows me to wrap the text of the icon in a div
  // which contains the groupId in the data-testid attribute
  // so that i can assert the icon has disappeared, in integration tests
  const markerHTML = ReactDOMServer.renderToStaticMarkup(
    <div data-marker-groupid={listing.groupId}>
      {formatCurrency({
        amount: listing.price,
        currency: listing.currency,
        lang,
      })}
    </div>,
  );

  const refugeeListingsMarkerHTML = ReactDOMServer.renderToStaticMarkup(
    <div data-marker-groupid={listing.groupId} />,
  );

  const markerIcon = L.divIcon({
    iconSize: [70, 40],
    iconAnchor: [35, 40],
    popupAnchor: [0, -30],
    html: markerHTML,
    className: highlight ? "MarkerIcon is-highlighted" : "MarkerIcon",
    id: `MarkerIcon ${listing.groupId}`,
  });

  const refugeeListingsMarkerIcon = L.divIcon({
    iconSize: highlight || isMarkerActive ? [59, 64] : [55, 62],
    iconAnchor: highlight || isMarkerActive ? [30, 30] : [30, 25],
    popupAnchor: [-10, 5],
    html: refugeeListingsMarkerHTML,
    className: `RefugeeListingMarkerIcon ${isMarkerActive ? "is-selected" : ""} ${
      highlight ? "is-highlighted" : ""
    }`,
    id: `RefugeeListingMarkerIcon ${listing.groupId}`,
  });

  let listingCard = (
    <article className="ListingMarker">
      {listing.restrictions && listing.restrictions.onlyStudentsAllowed && (
        <StudentExclusivePill short t={t} />
      )}
      <ImageGallery
        images={listing.pictures}
        showThumbnails={false}
        keyboardNavigation={false}
        onSelectImage={handleSelectImage}
        coverImage={listing.coverImage}
      />
      {canModifyWishlist && (
        <ShortlistButton
          t={t}
          groupId={listing.groupId}
          url={urlMaker("shortlist")}
          className="ListMarker-shortlistBtn"
          eventAction="Added Listing from Map"
        />
      )}
      <h3 className="ListingMarker-rent">
        <strong>{`${formatCurrency({
          amount: listing.price,
          currency: listing.currency,
          lang,
        })}`}</strong>
        <span className="ListingMarker-listingPrice">
          {t("spages.landlord.Calculator.Price.perMonth")}
        </span>
        <a
          href={getListingUrl(listing)}
          onClick={() => handleClickListing(listing)}
          target="_blank"
          className="Listing-link"
        >
          <strong className="Listing-linkText">
            {getListingTitle(listing)}
          </strong>
        </a>
      </h3>

      <div className="ListingMarker-details">
        <ListingDetailsBrief t={t} listing={listing} />
      </div>
    </article>
  );

  if (trackable) {
    listingCard = trackableListingCard(listingCard, trackingCallback);
  }

  return (
    <Marker
      ref={markerRef}
      listing={listing.groupId}
      position={position}
      icon={
        isHomesForUkrainiansCategory ? refugeeListingsMarkerIcon : markerIcon
      }
      eventHandlers={{
        ...(onClick ? { click: () => onClick() } : {}),
        mouseover: handleHover,
        mouseout: handleLeaveHover,
        popupopen: () => handlePopup(true),
        popupclose: () => handlePopup(false),
      }}
    >
      {listingCard && !isSmallScreen && (
        <Popup
          ref={popupRef}
          className={classnames("ListingPopup", {
            RefugeeListingPopup: isHomesForUkrainiansCategory,
          })}
        >
          {listingCard}
        </Popup>
      )}
    </Marker>
  );
};

ListingMarker.propTypes = propTypes;

export default ListingMarker;
